import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Sparkles, Check, Pencil } from 'lucide-react';
import { formatUnit, unitAxisSuffix } from '@/lib/units';

/**
 * Unit override pill for a measure on the Semantic Models page.
 *
 * Props:
 *   value:       string | null   — current persisted unit code (empty/null = use auto-detect)
 *   detected:    string          — what the auto-detector thinks the unit is (mirrors backend heuristic)
 *   onChange:    (newUnit) => Promise  — called with the chosen code (empty string = clear override)
 *   compact:     bool            — show shorter label (for inline use in entity attribute rows)
 *
 * Renders a tiny pill button → opens a click-away menu of unit choices.
 * The active selection (override OR detected) is highlighted; choosing
 * "Auto-detect" clears the override.
 */

// Mirrors `_UNIT_PATTERNS` in /app/backend/explore_advanced.py — used as
// a UI fallback so the pill can show a sensible "auto" label even when
// the server hasn't echoed a detected unit. Kept conservative; in
// production the server-resolved value is preferred.
function detectUnitLocal(name = '', description = '', dataType = '') {
  const n = (name || '').toLowerCase();
  const full = `${n} ${(description || '').toLowerCase()} ${(dataType || '').toLowerCase()}`;
  if (/_m$|_mm$|_mln$/.test(n)) return 'USD_millions';
  if (/_k$|_thsd$/.test(n)) return 'USD_thousands';
  if (/in_millions|in millions|\$m\b/.test(full)) return 'USD_millions';
  if (/in_thousands|in thousands|\$k\b/.test(full)) return 'USD_thousands';
  if (/loss_ratio|combined_ratio|expense_ratio|gross_margin|retention_rate|conversion_rate|churn_rate|win_rate/.test(n)) return 'percent';
  if (/_pct$|_percent$|percentage/.test(n)) return 'percent';
  if (/\bratio$|_ratio$|^ratio_/.test(n)) return 'ratio';
  if (/\bcount$|_count$|num_|number_of|^count_/.test(n)) return 'count';
  if (/\bdays?$|_days?$|lag_days|cycle_days/.test(n)) return 'days';
  if (/\bhours?$|_hours?$|_hrs$|duration_hours/.test(n)) return 'hours';
  if (/\bscore$|_score$|^nps$|_nps$|\bindex$|_index$/.test(n)) return 'score';
  if (/premium|incurred_losses|paid_losses|reserves|earned|written|revenue|spend|cost|expense|profit|amount/.test(full)) return 'USD';
  return 'none';
}

// Display label + sample value preview per unit code.
const UNIT_OPTIONS = [
  { code: '',                label: 'Auto-detect',     help: 'Use the heuristic',                       sample: 12345.6 },
  { code: 'USD_millions',    label: 'USD millions ($M)', help: 'Value already scaled to millions',      sample: 1234.5 },
  { code: 'USD_thousands',   label: 'USD thousands ($K)', help: 'Value already scaled to thousands',    sample: 1234.5 },
  { code: 'USD',             label: 'USD ($)',         help: 'Full-precision dollars (auto K / M)',     sample: 1234567 },
  { code: 'percent',         label: 'Percent (0–100)', help: 'Stored as 70.23 → renders 70.2%',         sample: 70.23 },
  { code: 'percent_decimal', label: 'Percent (0–1)',   help: 'Stored as 0.7023 → renders 70.2%',        sample: 0.7023 },
  { code: 'ratio',           label: 'Ratio (×)',       help: 'Multiplicative ratio, renders 1.23x',     sample: 1.23 },
  { code: 'count',           label: 'Count',           help: 'Plain integers',                          sample: 12345 },
  { code: 'days',            label: 'Days',            help: '1.2 days',                                sample: 1.23 },
  { code: 'hours',           label: 'Hours',           help: '1.2 hrs',                                 sample: 1.23 },
  { code: 'score',           label: 'Score',           help: 'Generic score (NPS, index)',              sample: 47.5 },
  { code: 'none',            label: 'None / raw',      help: 'No formatting applied',                   sample: 1234.56 },
];

const SHORT_LABELS = {
  '': 'auto',
  USD_millions: '$M',
  USD_thousands: '$K',
  USD: '$',
  percent: '%',
  percent_decimal: '% (0-1)',
  ratio: '×',
  count: '#',
  days: 'days',
  hours: 'hrs',
  score: 'score',
  none: '—',
};

export default function MeasureUnitPill({ value, detected, name = '', description = '', dataType = '', onChange, compact = false }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, maxHeight: 340, openUp: false });
  const btnRef = useRef(null);

  // Resolve the displayed unit: explicit override > server-detected > local-detect fallback.
  const resolvedDetected = useMemo(
    () => detected || detectUnitLocal(name, description, dataType),
    [detected, name, description, dataType]
  );
  const effective = value || resolvedDetected || 'none';
  const isOverride = !!value;
  const shortLabel = SHORT_LABELS[effective] ?? effective;

  // Position the popover relative to the pill on open. Flip it above the pill
  // when there isn't enough room below (e.g. a measure row near the bottom of
  // the screen) so the menu is never cut off by the viewport.
  useEffect(() => {
    if (open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const MENU_H = 340;
      const margin = 8;
      const spaceBelow = window.innerHeight - rect.bottom - margin;
      const spaceAbove = rect.top - margin;
      const openUp = spaceBelow < Math.min(MENU_H, 260) && spaceAbove > spaceBelow;
      const maxHeight = Math.max(180, Math.min(MENU_H, openUp ? spaceAbove : spaceBelow));
      const top = openUp
        ? rect.top + window.scrollY - maxHeight - 4
        : rect.bottom + window.scrollY + 4;
      setPos({
        top: Math.max(margin + window.scrollY, top),
        left: rect.left + window.scrollX,
        maxHeight,
        openUp,
      });
    }
  }, [open]);

  const handlePick = async (code) => {
    if (saving) return;
    setSaving(true);
    try {
      await onChange(code);
      setOpen(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <button
        ref={btnRef}
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-medium border transition-colors ${
          isOverride
            ? 'bg-cyan-500/15 text-cyan-300 border-cyan-500/30 hover:bg-cyan-500/25'
            : 'bg-muted/30 text-muted-foreground border-border/40 hover:text-foreground hover:bg-muted/40'
        }`}
        title={isOverride ? `Unit override: ${effective}` : `Auto-detected: ${effective}`}
        data-testid={`unit-pill-${name || 'measure'}`}
      >
        {!isOverride && <Sparkles className="w-2.5 h-2.5" />}
        <span>{shortLabel}</span>
        {!compact && <Pencil className="w-2.5 h-2.5 opacity-50" />}
      </button>

      {open && createPortal(
        <>
          <div className="fixed inset-0 z-[199]" onClick={() => setOpen(false)} data-testid="unit-pill-backdrop" />
          <div
            className="fixed z-[200] w-72 rounded-xl border border-border/50 shadow-2xl bg-popover overflow-hidden"
            style={{
              top: pos.top,
              left: Math.min(pos.left, window.innerWidth - 300),
              maxHeight: pos.maxHeight,
              background: 'var(--glass-bg, rgba(15,20,40,0.96))',
              backdropFilter: 'blur(16px)',
            }}
            data-testid="unit-pill-menu"
          >
            <div className="px-3 py-2 border-b border-border/30">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Display unit</p>
              <p className="text-[10px] text-muted-foreground/70 mt-0.5">
                Pick how this measure is formatted in charts, KPIs, and exports.
              </p>
            </div>
            <div className="overflow-y-auto" style={{ maxHeight: Math.max(120, pos.maxHeight - 56) }}>
              {UNIT_OPTIONS.map(opt => {
                const isActive = (opt.code || '') === (value || '');
                const isAutoOption = opt.code === '';
                const previewCode = isAutoOption ? resolvedDetected : opt.code;
                const preview = formatUnit(opt.sample, previewCode);
                const suffix = unitAxisSuffix(previewCode);
                return (
                  <button
                    key={opt.code}
                    onClick={() => handlePick(opt.code)}
                    disabled={saving}
                    className={`w-full flex items-start gap-2 px-3 py-2 text-left transition-colors ${isActive ? 'bg-cyan-500/10' : 'hover:bg-muted/30'} disabled:opacity-50`}
                    data-testid={`unit-pill-option-${opt.code || 'auto'}`}
                  >
                    <Check className={`w-3 h-3 mt-0.5 flex-shrink-0 ${isActive ? 'text-cyan-300' : 'text-transparent'}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className={`text-xs ${isActive ? 'text-cyan-200 font-semibold' : 'text-foreground'}`}>
                          {opt.label}
                          {isAutoOption && resolvedDetected !== 'none' && (
                            <span className="ml-1 text-[10px] text-muted-foreground">→ {resolvedDetected}</span>
                          )}
                        </p>
                        <span className="text-[10px] text-cyan-400/80 font-mono tabular-nums whitespace-nowrap">{preview || '—'}</span>
                      </div>
                      <p className="text-[10px] text-muted-foreground/70 leading-tight mt-0.5">
                        {opt.help}{suffix ? ` · axis: ${suffix}` : ''}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </>,
        document.body
      )}
    </>
  );
}
